import {
  type BackButtonListener,
  App as CapacitorApp,
  type RestoredListener,
  type StateChangeListener,
  type URLOpenListener,
} from "@capacitor/app";
import { Capacitor } from "@/lib/capacitor";
import { logger } from "@/logger";

export const App = (function () {
  const appLogger = logger.getLogger("@capacitor/app");

  async function exitApp() {
    return await CapacitorApp.exitApp();
  }

  async function getInfo() {
    if (Capacitor.Platform === "web") {
      appLogger.trace("App.getInfo() is not available on Web");
      return;
    }

    return await CapacitorApp.getInfo();
  }

  async function getState() {
    return (await CapacitorApp.getState()).isActive;
  }

  async function getLaunchUrl() {
    return await CapacitorApp.getLaunchUrl();
  }

  async function minimizeApp() {
    return await CapacitorApp.minimizeApp();
  }

  async function openSettings() {
    if (Capacitor.Platform === "web") {
      appLogger.trace("App.openSettings() is not available on Web");
      return;
    }

    if (Capacitor.Platform === "ios") {
      // iOS uses UIApplication.openSettingsURLString
      // This requires native implementation
      appLogger.info("Opening iOS app settings");
      // TODO: Implement native iOS method to open settings
      window.open("app-settings:", "_system");
    } else if (Capacitor.Platform === "android") {
      // Android requires a plugin or native implementation
      // For now, we'll log a warning
      appLogger.warn("App.openSettings() requires native implementation on Android");
      return;
    }
  }

  function addStateChangeListener(listenerFunc: StateChangeListener) {
    return CapacitorApp.addListener("appStateChange", listenerFunc);
  }

  function addUrlOpenListener(listenerFunc: URLOpenListener) {
    return CapacitorApp.addListener("appUrlOpen", listenerFunc);
  }

  function addRestoredResultListener(listenerFunc: RestoredListener) {
    return CapacitorApp.addListener("appRestoredResult", listenerFunc);
  }

  function addBackButtonListener(listenerFunc: BackButtonListener) {
    return CapacitorApp.addListener("backButton", listenerFunc);
  }

  function removeAllListeners() {
    return CapacitorApp.removeAllListeners();
  }

  return {
    ...CapacitorApp,
    exitApp,
    getInfo,
    getState,
    getLaunchUrl,
    minimizeApp,
    openSettings,
    addStateChangeListener,
    addUrlOpenListener,
    addRestoredResultListener,
    addBackButtonListener,
    removeAllListeners,
  };
})();

export type * from "@capacitor/app";
